import { useSyncExternalStore } from "react";

/**
 * `true` when motion must be reduced, from either trigger:
 *   - the OS preference `prefers-reduced-motion: reduce`
 *   - the app-level attribute `html[data-motion="reduced"]` (Settings toggle)
 *
 * Primitive stylesheets treat both identically; this hook gives the
 * JS-driven springs (thumb travel) the same answer. Stays in sync when either
 * source changes while the app is open.
 */

const QUERY = "(prefers-reduced-motion: reduce)";
const ATTRIBUTE = "data-motion";
const REDUCED = "reduced";

function subscribe(notify: () => void): () => void {
    if (typeof window === "undefined") {
        return () => undefined;
    }
    const cleanups: (() => void)[] = [];

    if (typeof window.matchMedia === "function") {
        const mql = window.matchMedia(QUERY);
        mql.addEventListener("change", notify);
        cleanups.push(() => mql.removeEventListener("change", notify));
    }

    if (typeof MutationObserver === "function") {
        const observer = new MutationObserver(notify);
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: [ATTRIBUTE],
        });
        cleanups.push(() => observer.disconnect());
    }

    return () => {
        for (const cleanup of cleanups) cleanup();
    };
}

function getSnapshot(): boolean {
    if (typeof document === "undefined") {
        return false;
    }
    if (document.documentElement.getAttribute(ATTRIBUTE) === REDUCED) {
        return true;
    }
    return typeof window.matchMedia === "function" && window.matchMedia(QUERY).matches;
}

function getServerSnapshot(): boolean {
    return false;
}

export function useReducedMotionPreference(): boolean {
    return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
